/**
 * Le impostazioni degli schermi in gelateria.
 *
 * Sta qui e non nella pagina Banco di proposito: il Banco vive su un tablet
 * appoggiato al bancone, davanti ai clienti e sotto le mani di chi serve. La
 * configurazione degli schermi — gli indirizzi, i file, il ritmo delle pagine —
 * non ha niente da fare su quel tablet, e la tocca chi gestisce.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Monitor, Copy, Trash2, Plus, Upload, Film, Image as Icona } from 'lucide-react';
import ApiService, {
  SchermoMonitor, MediaMonitor, PaginaMonitor, PuntoVenditaBanco
} from '../services/api';
import {
  PageContainerWide, PageHeader, Card, CardHeader, CardBody, Button,
  Alert, FormGroup, Label, Input, LoadingState, Badge
} from '../components/ui';

const Sezione = styled(Card)`
  margin-bottom: var(--space-lg);
`;

const Riga = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: var(--space-md);

  ${FormGroup} { margin-bottom: 0; }
`;

const Select = styled.select`
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--gray-300);
  min-width: 160px;
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th {
    text-align: left;
    padding: var(--space-sm) var(--space-md);
    border-bottom: 2px solid var(--gray-200);
    color: var(--text-secondary);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  td {
    padding: var(--space-sm) var(--space-md);
    border-bottom: 1px solid var(--gray-100);
    vertical-align: middle;
  }

  code { font-size: var(--font-size-xs); word-break: break-all; color: var(--text-secondary); }
`;

const Anteprima = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);

  img, video {
    width: 96px;
    height: 54px;
    object-fit: cover;
    border-radius: var(--radius-sm);
    background: var(--gray-100);
  }
`;

const Categorie = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm) var(--space-md);
  padding: var(--space-sm) 0;
  max-width: 520px;

  label {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    white-space: nowrap;
  }
`;

const PiccolaNota = styled.small`
  color: var(--text-light);
`;

const Nota = styled.p`
  color: var(--text-light);
  font-size: var(--font-size-sm);
`;

const megabyte = (byte: string | null): string =>
  byte ? `${(Number(byte) / 1024 / 1024).toFixed(1)} MB` : '—';

const secondi = (valore: string | null): string =>
  valore ? `${Math.round(Number(valore))} s` : '—';

const ImpostazioniMonitorPage: React.FC = () => {
  const [schermi, setSchermi] = useState<SchermoMonitor[]>([]);
  const [punti, setPunti] = useState<PuntoVenditaBanco[]>([]);
  const [media, setMedia] = useState<MediaMonitor[]>([]);
  const [categorie, setCategorie] = useState<string[]>([]);
  const [schermoAperto, setSchermoAperto] = useState<number | null>(null);
  const [pagine, setPagine] = useState<PaginaMonitor[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [inCaricamentoFile, setInCaricamentoFile] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [copiato, setCopiato] = useState<number | null>(null);
  // La pagina che si sta modificando, con i valori in corso di modifica: prima
  // per cambiare un tempo bisognava cancellare la pagina e rifarla.
  const [inModifica, setInModifica] = useState<{
    id: number; titolo: string; categorie: string[]; mediaId: number | ''; secondi: string;
  } | null>(null);

  const [nuovoSchermo, setNuovoSchermo] = useState('');
  const [puntoNuovoSchermo, setPuntoNuovoSchermo] = useState<number | ''>('');
  const [nuovaPagina, setNuovaPagina] = useState<{
    tipo: 'gusti' | 'media'; categorie: string[]; mediaId: number | ''; secondi: string;
  }>({ tipo: 'gusti', categorie: [], mediaId: '', secondi: '' });

  const carica = useCallback(async () => {
    try {
      const [s, m, c, p] = await Promise.all([
        ApiService.getSchermiMonitor(),
        ApiService.getMediaMonitor(),
        ApiService.getCategorieMonitor(),
        ApiService.getPuntiVenditaBanco()
      ]);
      setSchermi(s);
      setMedia(m);
      setCategorie(c);
      setPunti(p);
      setPuntoNuovoSchermo((precedente) => (precedente === '' && p.length ? p[0].id : precedente));
      setErrore(null);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel caricamento delle impostazioni');
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const apriPagine = async (schermoId: number) => {
    setSchermoAperto(schermoId);
    setPagine(await ApiService.getPagineMonitor(schermoId));
  };

  const ricaricaPagine = async () => {
    if (schermoAperto) setPagine(await ApiService.getPagineMonitor(schermoAperto));
  };

  const indirizzo = (schermo: SchermoMonitor) => `${window.location.origin}/monitor/${schermo.token}`;

  const copia = async (schermo: SchermoMonitor) => {
    try {
      await navigator.clipboard.writeText(indirizzo(schermo));
      setCopiato(schermo.id);
      setTimeout(() => setCopiato(null), 2000);
    } catch {
      setErrore('Copia non riuscita: seleziona l\'indirizzo e copialo a mano');
    }
  };

  const creaSchermo = async () => {
    if (!nuovoSchermo.trim() || puntoNuovoSchermo === '') return;
    try {
      setErrore(null);
      await ApiService.creaSchermoMonitor({
        nome: nuovoSchermo.trim(), puntoVenditaId: Number(puntoNuovoSchermo)
      });
      setNuovoSchermo('');
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nella creazione dello schermo');
    }
  };

  const cambiaSchermo = async (id: number, dati: any) => {
    try {
      setErrore(null);
      await ApiService.aggiornaSchermoMonitor(id, dati);
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel salvataggio');
    }
  };

  const caricaFile = async (file: File | undefined) => {
    if (!file) return;
    setInCaricamentoFile(true);
    setErrore(null);
    try {
      await ApiService.caricaMediaMonitor(file, file.name.replace(/\.[^.]+$/, ''));
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel caricamento del file');
    } finally {
      setInCaricamentoFile(false);
    }
  };

  const aggiungiPagina = async () => {
    if (!schermoAperto) return;
    if (nuovaPagina.tipo === 'media' && nuovaPagina.mediaId === '') {
      setErrore('Scegli il file da mostrare');
      return;
    }
    try {
      setErrore(null);
      await ApiService.creaPaginaMonitor(schermoAperto, {
        tipo: nuovaPagina.tipo,
        categorie: nuovaPagina.tipo === 'gusti' ? nuovaPagina.categorie : null,
        mediaId: nuovaPagina.mediaId === '' ? null : Number(nuovaPagina.mediaId),
        secondi: nuovaPagina.secondi ? Number(nuovaPagina.secondi) : null
      });
      setNuovaPagina({ tipo: 'gusti', categorie: [], mediaId: '', secondi: '' });
      await ricaricaPagine();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nella creazione della pagina');
    }
  };

  const apriModifica = (pagina: PaginaMonitor) => setInModifica({
    id: pagina.id,
    titolo: pagina.titolo || '',
    categorie: pagina.categorie || [],
    mediaId: pagina.media_id ?? '',
    secondi: pagina.secondi ? String(pagina.secondi) : ''
  });

  const salvaModifica = async () => {
    if (!inModifica) return;
    try {
      setErrore(null);
      await ApiService.aggiornaPaginaMonitor(inModifica.id, {
        titolo: inModifica.titolo,
        categorie: inModifica.categorie,
        // null non basta a svuotare un campo: il backend lo legge come "non
        // toccare". Lo zero e' il modo per dire "torna al predefinito".
        mediaId: inModifica.mediaId === '' ? null : Number(inModifica.mediaId),
        secondi: inModifica.secondi ? Number(inModifica.secondi) : null
      });
      setInModifica(null);
      await ricaricaPagine();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel salvataggio della pagina');
    }
  };

  const spostaPagina = async (pagina: PaginaMonitor, direzione: -1 | 1) => {
    const ordinate = [...pagine].sort((a, b) => a.ordine - b.ordine);
    const posizione = ordinate.findIndex((p) => p.id === pagina.id);
    const vicina = ordinate[posizione + direzione];
    if (!vicina) return;
    await ApiService.aggiornaPaginaMonitor(pagina.id, { ordine: vicina.ordine });
    await ApiService.aggiornaPaginaMonitor(vicina.id, { ordine: pagina.ordine });
    await ricaricaPagine();
  };

  if (caricamento) return <PageContainerWide><LoadingState /></PageContainerWide>;

  const schermo = schermi.find((s) => s.id === schermoAperto) || null;

  return (
    <PageContainerWide>
      <PageHeader
        title="Monitor"
        subtitle="Gli schermi in gelateria: cosa mostrano, con che sfondo e con che ritmo."
        icon={<Monitor size={24} />}
      />

      {errore && <Alert $variant="error">{errore}</Alert>}

      <Sezione>
        <CardHeader><h3>Schermi</h3></CardHeader>
        <CardBody>
          <Riga>
            <FormGroup>
              <Label htmlFor="nuovo-schermo">Nuovo schermo</Label>
              <Input id="nuovo-schermo" value={nuovoSchermo} placeholder="Monitor vetrina"
                onChange={(e) => setNuovoSchermo(e.target.value)} />
            </FormGroup>
            {punti.length > 1 && (
              <FormGroup>
                <Label htmlFor="pv-schermo">Punto vendita</Label>
                <Select id="pv-schermo" value={puntoNuovoSchermo}
                  onChange={(e) => setPuntoNuovoSchermo(e.target.value ? Number(e.target.value) : '')}>
                  {punti.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </Select>
              </FormGroup>
            )}
            <Button $variant="primary" onClick={creaSchermo}
              disabled={!nuovoSchermo.trim() || puntoNuovoSchermo === ''}>
              <Plus size={16} /> Aggiungi
            </Button>
          </Riga>

          {!schermi.length && (
            <Nota>
              Nessuno schermo. Creane uno, poi apri il suo indirizzo sul monitor in gelateria:
              la pagina mostra i gusti in vetrina e si aggiorna da sola.
            </Nota>
          )}

          {!!schermi.length && (
            <Tabella>
              <thead>
                <tr>
                  <th>Schermo</th><th>Indirizzo</th><th>Cambio pagina</th>
                  <th>Due lingue</th><th>Sfondo</th><th>Ultimo contatto</th><th />
                </tr>
              </thead>
              <tbody>
                {schermi.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.nome}
                      {s.punto_vendita_nome && (
                        <><br /><span style={{ color: 'var(--text-light)' }}>{s.punto_vendita_nome}</span></>
                      )}
                      {!s.attivo && <> <Badge $variant="neutral">spento</Badge></>}
                    </td>
                    <td><code>{indirizzo(s)}</code></td>
                    <td>
                      <Input type="number" min={3} max={300} style={{ width: '5.5rem' }}
                        defaultValue={s.secondi_pagina ?? 15}
                        onBlur={(e) => {
                          const valore = Number(e.target.value);
                          if (valore && valore !== s.secondi_pagina) {
                            cambiaSchermo(s.id, { secondiPagina: valore });
                          }
                        }} /> s
                    </td>
                    <td>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input type="checkbox" checked={s.alterna_nomi === true}
                          onChange={(e) => cambiaSchermo(s.id, { alternaNomi: e.target.checked })} />
                        alterna
                      </label>
                      {s.alterna_nomi && (
                        <>
                          <Input type="number" min={2} max={60} style={{ width: '4.5rem' }}
                            defaultValue={s.secondi_nome ?? 5}
                            onBlur={(e) => {
                              const valore = Number(e.target.value);
                              if (valore && valore !== s.secondi_nome) {
                                cambiaSchermo(s.id, { secondiNome: valore });
                              }
                            }} /> s
                        </>
                      )}
                    </td>
                    <td>
                      <Select value={s.media_sfondo_id ?? ''}
                        onChange={(e) => cambiaSchermo(s.id, {
                          mediaSfondoId: e.target.value ? Number(e.target.value) : null
                        })}>
                        <option value="">nessuno</option>
                        {media.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                      </Select>
                    </td>
                    <td>
                      {s.ultimo_accesso_at
                        ? new Date(s.ultimo_accesso_at).toLocaleString('it-IT')
                        : 'mai'}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button $variant="secondary" $size="sm" onClick={() => copia(s)}>
                        <Copy size={14} /> {copiato === s.id ? 'Copiato' : 'Copia'}
                      </Button>{' '}
                      <Button $variant="secondary" $size="sm" onClick={() => apriPagine(s.id)}>
                        Pagine
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          )}
        </CardBody>
      </Sezione>

      <Sezione>
        <CardHeader><h3>Immagini e video</h3></CardHeader>
        <CardBody>
          <Riga>
            <label>
              <Button as="span" $variant="primary" style={{ cursor: 'pointer' }}>
                <Upload size={16} /> {inCaricamentoFile ? 'Conversione in corso…' : 'Carica un file'}
              </Button>
              <input type="file" accept="image/*,video/*" style={{ display: 'none' }}
                disabled={inCaricamentoFile}
                onChange={(e) => { caricaFile(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          </Riga>

          <Nota>
            Il file viene riconvertito una volta sola: 1920×1080, senza audio, in un formato che
            tutti i player riproducono. L'originale non viene conservato, quindi carica pure
            l'export pesante che esce da Canva. Un video lungo può richiedere qualche minuto.
          </Nota>

          {!!media.length && (
            <Tabella>
              <thead>
                <tr><th>File</th><th>Tipo</th><th>Durata</th><th>Peso</th><th /></tr>
              </thead>
              <tbody>
                {media.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Anteprima>
                        {m.tipo === 'video'
                          ? <video src={m.url} muted preload="metadata" />
                          : <img src={m.url} alt="" />}
                        {m.nome}
                      </Anteprima>
                    </td>
                    <td>{m.tipo === 'video' ? <><Film size={14} /> video</> : <><Icona size={14} /> immagine</>}</td>
                    <td>{secondi(m.durata_secondi)}</td>
                    <td>{megabyte(m.dimensione_byte)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button $variant="secondary" $size="sm"
                        onClick={async () => {
                          await ApiService.eliminaMediaMonitor(m.id);
                          await carica();
                          await ricaricaPagine();
                        }}>
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          )}
        </CardBody>
      </Sezione>

      {schermo && (
        <Sezione>
          <CardHeader><h3>Impaginazione di {schermo.nome}</h3></CardHeader>
          <CardBody>
            <Nota>
              Questi valori si regolano stando davanti allo schermo: dipendono da com'è
              montato. Se il monitor è incassato in una nicchia o dentro un arco, i margini
              tengono titolo e gusti lontani dagli angoli coperti. Colonne e righe lasciate
              a zero si arrangiano da sole; impostate a mano comandano loro, e decidono
              anche quanti gusti entrano in una schermata.
            </Nota>

            <Riga>
              <FormGroup>
                <Label htmlFor="gusti-pagina">Gusti per schermata</Label>
                <Input id="gusti-pagina" type="number" min={2} max={24} style={{ width: '6rem' }}
                  defaultValue={schermo.gusti_per_pagina ?? 8}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore && valore !== schermo.gusti_per_pagina) {
                      cambiaSchermo(schermo.id, { gustiPerPagina: valore });
                    }
                  }} />
                <PiccolaNota>
                  {(schermo.colonne ?? 0) > 0 && (schermo.righe ?? 0) > 0
                    ? `ignorato: comanda la griglia, ${schermo.colonne}×${schermo.righe} = `
                      + `${(schermo.colonne || 0) * (schermo.righe || 0)} gusti`
                    : 'oltre questo numero la pagina si divide da sola'}
                </PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="scala">Dimensione</Label>
                <Input id="scala" type="number" min={50} max={200} step={5} style={{ width: '6rem' }}
                  defaultValue={schermo.scala ?? 100}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore && valore !== schermo.scala) {
                      cambiaSchermo(schermo.id, { scala: valore });
                    }
                  }} />
                <PiccolaNota>percentuale: 100 è la misura di riferimento</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="margine-alto">Margine in alto</Label>
                <Input id="margine-alto" type="number" min={0} max={40} style={{ width: '6rem' }}
                  defaultValue={schermo.margine_alto ?? 0}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.margine_alto) {
                      cambiaSchermo(schermo.id, { margineAlto: valore });
                    }
                  }} />
                <PiccolaNota>% dell'altezza</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="velo">Copertura dello sfondo</Label>
                <Input id="velo" type="number" min={0} max={100} step={5} style={{ width: '6rem' }}
                  defaultValue={schermo.velo_sfondo ?? 78}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.velo_sfondo) {
                      cambiaSchermo(schermo.id, { veloSfondo: valore });
                    }
                  }} />
                <PiccolaNota>0 = sfondo pulito, 100 = sfondo invisibile</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="margine-lati">Margine ai lati</Label>
                <Input id="margine-lati" type="number" min={0} max={40} style={{ width: '6rem' }}
                  defaultValue={schermo.margine_lati ?? 0}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.margine_lati) {
                      cambiaSchermo(schermo.id, { margineLati: valore });
                    }
                  }} />
                <PiccolaNota>% della larghezza</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="colonne">Colonne</Label>
                <Input id="colonne" type="number" min={0} max={8} style={{ width: '6rem' }}
                  defaultValue={schermo.colonne ?? 0}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.colonne) {
                      cambiaSchermo(schermo.id, { colonne: valore });
                    }
                  }} />
                <PiccolaNota>0 = decide da solo</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="righe">Righe</Label>
                <Input id="righe" type="number" min={0} max={6} style={{ width: '6rem' }}
                  defaultValue={schermo.righe ?? 0}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.righe) {
                      cambiaSchermo(schermo.id, { righe: valore });
                    }
                  }} />
                <PiccolaNota>con le colonne decide quanti gusti per schermata</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="spazio-v">Distanza fra le righe</Label>
                <Input id="spazio-v" type="number" min={0} max={300} step={10} style={{ width: '6rem' }}
                  defaultValue={schermo.spazio_verticale ?? 100}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.spazio_verticale) {
                      cambiaSchermo(schermo.id, { spazioVerticale: valore });
                    }
                  }} />
                <PiccolaNota>percentuale: 100 è la distanza di riferimento</PiccolaNota>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="spazio-o">Distanza fra le colonne</Label>
                <Input id="spazio-o" type="number" min={0} max={300} step={10} style={{ width: '6rem' }}
                  defaultValue={schermo.spazio_orizzontale ?? 100}
                  onBlur={(e) => {
                    const valore = Number(e.target.value);
                    if (valore !== schermo.spazio_orizzontale) {
                      cambiaSchermo(schermo.id, { spazioOrizzontale: valore });
                    }
                  }} />
                <PiccolaNota>percentuale: 100 è la distanza di riferimento</PiccolaNota>
              </FormGroup>
            </Riga>
          </CardBody>
        </Sezione>
      )}

      {schermo && (
        <Sezione>
          <CardHeader>
            <h3>Pagine di {schermo.nome}</h3>
            <Button $variant="secondary" $size="sm" onClick={() => setSchermoAperto(null)}>Chiudi</Button>
          </CardHeader>
          <CardBody>
            <Nota>
              Senza pagine lo schermo mostra una pagina per categoria di gusti, in automatico.
              Appena ne aggiungi una, comanda questo elenco.
            </Nota>

            <Riga>
              <FormGroup>
                <Label htmlFor="tipo-pagina">Tipo</Label>
                <Select id="tipo-pagina" value={nuovaPagina.tipo}
                  onChange={(e) => setNuovaPagina({ ...nuovaPagina, tipo: e.target.value as 'gusti' | 'media' })}>
                  <option value="gusti">Gusti in vetrina</option>
                  <option value="media">Solo immagine o video</option>
                </Select>
              </FormGroup>

              {nuovaPagina.tipo === 'gusti' && (
                <FormGroup>
                  <Label>Categorie</Label>
                  <Categorie>
                    {categorie.map((c) => (
                      <label key={c}>
                        <input type="checkbox" checked={nuovaPagina.categorie.includes(c)}
                          onChange={(e) => setNuovaPagina({
                            ...nuovaPagina,
                            categorie: e.target.checked
                              ? [...nuovaPagina.categorie, c]
                              : nuovaPagina.categorie.filter((x) => x !== c)
                          })} />
                        {c}
                      </label>
                    ))}
                  </Categorie>
                  <PiccolaNota>
                    {nuovaPagina.categorie.length
                      ? 'Le categorie scelte finiscono nella stessa pagina.'
                      : 'Nessuna scelta: la pagina mostra tutti i gusti in vetrina.'}
                  </PiccolaNota>
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="media-pagina">
                  {nuovaPagina.tipo === 'media' ? 'File da mostrare' : 'Sfondo'}
                </Label>
                <Select id="media-pagina" value={nuovaPagina.mediaId}
                  onChange={(e) => setNuovaPagina({
                    ...nuovaPagina, mediaId: e.target.value ? Number(e.target.value) : ''
                  })}>
                  <option value="">{nuovaPagina.tipo === 'media' ? 'scegli…' : 'quello dello schermo'}</option>
                  {media.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="secondi-pagina">Durata</Label>
                <Input id="secondi-pagina" type="number" min={3} max={600} style={{ width: '6rem' }}
                  placeholder="auto" value={nuovaPagina.secondi}
                  onChange={(e) => setNuovaPagina({ ...nuovaPagina, secondi: e.target.value })} />
              </FormGroup>

              <Button $variant="primary" onClick={aggiungiPagina}><Plus size={16} /> Aggiungi</Button>
            </Riga>

            {!!pagine.length && (
              <Tabella>
                <thead>
                  <tr><th>Pagina</th><th>Contenuto</th><th>Durata</th><th /></tr>
                </thead>
                <tbody>
                  {[...pagine].sort((a, b) => a.ordine - b.ordine).map((pagina) => (
                    inModifica && inModifica.id === pagina.id ? (
                      <tr key={pagina.id}>
                        <td colSpan={4}>
                          <Riga>
                            <FormGroup>
                              <Label htmlFor={`titolo-${pagina.id}`}>Titolo</Label>
                              <Input id={`titolo-${pagina.id}`} value={inModifica.titolo}
                                placeholder="automatico"
                                onChange={(e) => setInModifica({ ...inModifica, titolo: e.target.value })} />
                            </FormGroup>

                            <FormGroup>
                              <Label htmlFor={`media-${pagina.id}`}>
                                {pagina.tipo === 'media' ? 'File' : 'Sfondo'}
                              </Label>
                              <Select id={`media-${pagina.id}`} value={inModifica.mediaId}
                                onChange={(e) => setInModifica({
                                  ...inModifica, mediaId: e.target.value ? Number(e.target.value) : ''
                                })}>
                                <option value="">
                                  {pagina.tipo === 'media' ? 'scegli…' : 'quello dello schermo'}
                                </option>
                                {media.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                              </Select>
                            </FormGroup>

                            <FormGroup>
                              <Label htmlFor={`secondi-${pagina.id}`}>Durata</Label>
                              <Input id={`secondi-${pagina.id}`} type="number" min={3} max={600}
                                style={{ width: '6rem' }} placeholder="auto" value={inModifica.secondi}
                                onChange={(e) => setInModifica({ ...inModifica, secondi: e.target.value })} />
                            </FormGroup>

                            <Button $variant="primary" onClick={salvaModifica}>Salva</Button>
                            <Button $variant="secondary" onClick={() => setInModifica(null)}>Annulla</Button>
                          </Riga>

                          {pagina.tipo === 'gusti' && (
                            <FormGroup>
                              <Label>Categorie</Label>
                              <Categorie>
                                {categorie.map((c) => (
                                  <label key={c}>
                                    <input type="checkbox" checked={inModifica.categorie.includes(c)}
                                      onChange={(e) => setInModifica({
                                        ...inModifica,
                                        categorie: e.target.checked
                                          ? [...inModifica.categorie, c]
                                          : inModifica.categorie.filter((x) => x !== c)
                                      })} />
                                    {c}
                                  </label>
                                ))}
                              </Categorie>
                              <PiccolaNota>
                                {inModifica.categorie.length
                                  ? 'Le categorie scelte finiscono nella stessa pagina.'
                                  : 'Nessuna scelta: la pagina mostra tutti i gusti in vetrina.'}
                              </PiccolaNota>
                            </FormGroup>
                          )}
                        </td>
                      </tr>
                    ) : (
                      <tr key={pagina.id}>
                        <td>
                          {pagina.tipo === 'gusti'
                            ? (pagina.titolo || pagina.categorie?.join(' · ') || 'Tutti i gusti')
                            : (pagina.titolo || 'Contenuto')}
                          {!pagina.attiva && <> <Badge $variant="neutral">nascosta</Badge></>}
                        </td>
                        <td>
                          {pagina.tipo === 'gusti'
                            ? `${pagina.categorie?.length ? pagina.categorie.join(' · ') : 'tutti i gusti'}${pagina.media_nome ? ` · sfondo ${pagina.media_nome}` : ''}`
                            : (pagina.media_nome || 'file mancante')}
                        </td>
                        <td>{pagina.secondi ? `${pagina.secondi} s` : 'automatica'}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <Button $variant="secondary" $size="sm" onClick={() => spostaPagina(pagina, -1)}>↑</Button>{' '}
                          <Button $variant="secondary" $size="sm" onClick={() => spostaPagina(pagina, 1)}>↓</Button>{' '}
                          <Button $variant="secondary" $size="sm" onClick={() => apriModifica(pagina)}>
                            Modifica
                          </Button>{' '}
                          <Button $variant="secondary" $size="sm"
                            onClick={async () => {
                              await ApiService.aggiornaPaginaMonitor(pagina.id, { attiva: !pagina.attiva });
                              await ricaricaPagine();
                            }}>
                            {pagina.attiva ? 'Nascondi' : 'Mostra'}
                          </Button>{' '}
                          <Button $variant="secondary" $size="sm"
                            onClick={async () => {
                              await ApiService.eliminaPaginaMonitor(pagina.id);
                              await ricaricaPagine();
                            }}>
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </Tabella>
            )}
          </CardBody>
        </Sezione>
      )}
    </PageContainerWide>
  );
};

export default ImpostazioniMonitorPage;
